import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PauseCircleRoundedIcon from "@mui/icons-material/PauseCircleRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import { useLocation, useParams } from "react-router-dom";
import { billingApi } from "../../api/billing";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import { isSuperAdmin } from "../../utils/roles";

const BRAND = {
  orange: "#F35A3A",
  orangeSoft: "#FFF2EE",
  dark: "#19242F",
  page: "#F6F7F9",
  border: "rgba(25,36,47,0.10)",
  muted: "rgba(25,36,47,0.62)",
};

const PANEL_SX = {
  borderRadius: 4,
  border: `1px solid ${BRAND.border}`,
  boxShadow: "0 16px 42px rgba(25,36,47,0.06)",
  bgcolor: "#FFFFFF",
};

function money(value, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function date(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(
    parsed,
  );
}

function statusMeta(status) {
  const normalized = String(status || "").toLowerCase();
  const map = {
    active: { label: "Activo", color: "success" },
    paid: { label: "Pagada", color: "success" },
    approved: { label: "Aprobado", color: "success" },
    exempt: { label: "Bonificado", color: "success" },
    open: { label: "Pendiente", color: "warning" },
    pending: { label: "Pendiente", color: "warning" },
    pending_activation: { label: "Pendiente de activación", color: "warning" },
    overdue: { label: "Vencida", color: "error" },
    past_due: { label: "Pago atrasado", color: "error" },
    rejected: { label: "Rechazado", color: "error" },
    suspended: { label: "Suspendido", color: "error" },
    cancelled: { label: "Cancelado", color: "default" },
  };

  return (
    map[normalized] || {
      label: normalized ? normalized.replaceAll("_", " ") : "Sin estado",
      color: "default",
    }
  );
}

function billingCycleLabel(value) {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "monthly") return "Mensual";
  if (normalized === "annual") return "Anual";
  if (normalized === "weekly") return "Semanal";
  return value || "Recurrente";
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function printInvoice(invoice, account, project) {
  const popup = window.open("", "_blank", "width=900,height=720");
  if (!popup) return;

  const rows = (invoice.lines || [])
    .map(
      (line) => `
        <tr>
          <td>${escapeHtml(line.description)}</td>
          <td style="text-align:right">${escapeHtml(line.quantity)}</td>
          <td style="text-align:right">${escapeHtml(
            money(line.unitAmount, invoice.currency),
          )}</td>
          <td style="text-align:right">${escapeHtml(
            money(line.total, invoice.currency),
          )}</td>
        </tr>`,
    )
    .join("");

  popup.document.write(`<!doctype html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>${escapeHtml(invoice.invoiceNumber)}</title>
        <style>
          *{box-sizing:border-box}
          body{font-family:Inter,Arial,sans-serif;color:#19242F;padding:42px;margin:0}
          h1,h2,p{margin:0}
          .muted{color:#66717d}
          .header{display:flex;justify-content:space-between;gap:30px;padding-bottom:26px;border-bottom:2px solid #19242F}
          .brand{font-size:30px;font-weight:900}
          .invoice-number{font-size:24px;font-weight:900;text-align:right}
          .badge{display:inline-block;margin-top:8px;padding:6px 11px;border-radius:999px;background:#fff2ee;color:#b7361f;font-weight:800}
          .row{display:flex;justify-content:space-between;gap:30px;margin:28px 0;line-height:1.7}
          table{width:100%;border-collapse:collapse;margin-top:28px}
          th{font-size:12px;text-transform:uppercase;letter-spacing:.08em;color:#66717d;background:#f5f6f8}
          th,td{padding:13px;border-bottom:1px solid #dde1e5;text-align:left}
          .totals{margin-left:auto;width:330px;margin-top:26px}
          .totals div{display:flex;justify-content:space-between;padding:8px 0}
          .total{font-size:21px;font-weight:900;border-top:2px solid #19242F;margin-top:6px;padding-top:14px!important}
          .notes{margin-top:34px;padding:16px;border-radius:12px;background:#f5f6f8}
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="brand">WebProfit</div>
            <div class="muted">Documento interno de cobro</div>
          </div>
          <div>
            <div class="invoice-number">${escapeHtml(invoice.invoiceNumber)}</div>
            <span class="badge">${escapeHtml(statusMeta(invoice.status).label)}</span>
          </div>
        </div>
        <div class="row">
          <div>
            <strong>Facturado a</strong><br>
            ${escapeHtml(account?.businessName || project?.name)}<br>
            ${escapeHtml(account?.billingEmail || "")}<br>
            ${escapeHtml(account?.taxId || "")}
          </div>
          <div style="text-align:right">
            <strong>Emisión:</strong> ${escapeHtml(date(invoice.issueDate))}<br>
            <strong>Vencimiento:</strong> ${escapeHtml(date(invoice.dueDate))}<br>
            <strong>Proyecto:</strong> ${escapeHtml(project?.name || "")}
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Servicio</th>
              <th style="text-align:right">Cant.</th>
              <th style="text-align:right">Precio</th>
              <th style="text-align:right">Total</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="totals">
          <div><span>Subtotal</span><strong>${escapeHtml(
            money(invoice.subtotal, invoice.currency),
          )}</strong></div>
          <div><span>Descuento</span><strong>${escapeHtml(
            money(invoice.discount, invoice.currency),
          )}</strong></div>
          <div><span>Impuestos</span><strong>${escapeHtml(
            money(invoice.tax, invoice.currency),
          )}</strong></div>
          <div class="total"><span>Total</span><span>${escapeHtml(
            money(invoice.total, invoice.currency),
          )}</span></div>
        </div>
        ${
          invoice.notes
            ? `<div class="notes"><strong>Observaciones</strong><br>${escapeHtml(
                invoice.notes,
              )}</div>`
            : ""
        }
        <script>window.onload=()=>window.print();</script>
      </body>
    </html>`);

  popup.document.close();
}

function SectionHeading({ eyebrow, title, description, action }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ sm: "flex-start" }}
      spacing={2}
    >
      <Box>
        {eyebrow ? (
          <Typography
            variant="overline"
            sx={{
              color: BRAND.orange,
              fontWeight: 900,
              letterSpacing: ".12em",
            }}
          >
            {eyebrow}
          </Typography>
        ) : null}
        <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" sx={{ mt: 0.6, color: BRAND.muted }}>
            {description}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Stack>
  );
}

function MetricCard({ icon, label, value, helper, tone = "default" }) {
  const toneStyles = {
    default: {
      background: "#FFFFFF",
      iconBg: "#F0F2F5",
      iconColor: BRAND.dark,
    },
    primary: {
      background: BRAND.orangeSoft,
      iconBg: "#FFFFFF",
      iconColor: BRAND.orange,
    },
    danger: { background: "#FFF5F4", iconBg: "#FFFFFF", iconColor: "#D14343" },
  };
  const currentTone = toneStyles[tone] || toneStyles.default;

  return (
    <Card
      elevation={0}
      sx={{
        ...PANEL_SX,
        height: "100%",
        background: currentTone.background,
        boxShadow: "none",
      }}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar
            variant="rounded"
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2.5,
              bgcolor: currentTone.iconBg,
              color: currentTone.iconColor,
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: BRAND.muted,
                fontWeight: 850,
                letterSpacing: ".03em",
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mt: 0.25,
                fontWeight: 950,
                color: BRAND.dark,
                lineHeight: 1.2,
              }}
            >
              {value}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.8, color: BRAND.muted }}>
              {helper}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ProjectBillingPage() {
  const { projectId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const { loadProjects, setActiveProject } = useProjects();
  const superAdmin = isSuperAdmin(user);

  const [data, setData] = React.useState(null);
  const [accountForm, setAccountForm] = React.useState({});
  const [subscriptionForm, setSubscriptionForm] = React.useState({});
  const [serviceForm, setServiceForm] = React.useState({
    serviceId: "",
    quantity: 1,
    unitAmount: "",
  });
  const [invoiceForm, setInvoiceForm] = React.useState({
    dueDays: 7,
    notes: "",
  });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await billingApi.getProject(projectId);
      setData(response);
      setActiveProject(response.project);
      setAccountForm({
        businessName:
          response.account?.businessName || response.project?.name || "",
        legalName: response.account?.legalName || "",
        taxId: response.account?.taxId || "",
        taxCondition: response.account?.taxCondition || "consumidor_final",
        billingEmail: response.account?.billingEmail || "",
        phone: response.account?.phone || "",
      });
      setSubscriptionForm({
        status: response.subscription?.status || "pending_activation",
        activationMode:
          response.subscription?.activationMode || "after_payment",
        autoSuspend: response.subscription?.autoSuspend !== false,
        graceDays: response.subscription?.graceDays ?? 5,
        renewalDay: response.subscription?.renewalDay ?? 1,
        currency: response.subscription?.currency || "ARS",
      });
    } catch (err) {
      setError(err.message || "No se pudo cargar la facturación del proyecto");
    } finally {
      setLoading(false);
    }
  }, [projectId, setActiveProject]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payment = params.get("payment");
    if (payment === "success") {
      setMessage(
        "Mercado Pago informó un pago aprobado. La conciliación se completará automáticamente cuando llegue el webhook.",
      );
    }
    if (payment === "pending") {
      setMessage(
        "El pago quedó pendiente. La factura se actualizará automáticamente cuando Mercado Pago confirme el resultado.",
      );
    }
    if (payment === "failure") {
      setError("El pago no se completó. Podés intentarlo nuevamente.");
    }
  }, [location.search]);

  const run = async (task, successMessage) => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await task();
      setMessage(successMessage);
      await load();
      await loadProjects();
    } catch (err) {
      setError(err.message || "No se pudo completar la operación");
    } finally {
      setSaving(false);
    }
  };

  const saveBilling = (event) => {
    event.preventDefault();
    run(
      () =>
        billingApi.updateProject(projectId, {
          account: accountForm,
          subscription: subscriptionForm,
        }),
      "Cuenta y reglas de activación actualizadas.",
    );
  };

  const selectedCatalogService = (data?.availableServices || []).find(
    (service) => service.id === serviceForm.serviceId,
  );

  const assignService = (event) => {
    event.preventDefault();
    if (!serviceForm.serviceId) return;

    run(
      () =>
        billingApi.assignService(projectId, {
          serviceId: serviceForm.serviceId,
          quantity: Number(serviceForm.quantity || 1),
          unitAmount:
            serviceForm.unitAmount === ""
              ? selectedCatalogService?.defaultAmount
              : Number(serviceForm.unitAmount),
        }),
      "Servicio agregado al abono.",
    );
    setServiceForm({ serviceId: "", quantity: 1, unitAmount: "" });
  };

  const createInvoice = (event) => {
    event.preventDefault();
    run(
      () =>
        billingApi.createInvoice(projectId, {
          dueDays: Number(invoiceForm.dueDays || 0),
          notes: invoiceForm.notes,
        }),
      "Factura emitida.",
    );
  };

  const payInvoice = async (invoice) => {
    try {
      setSaving(true);
      setError("");
      const response = await billingApi.createCheckout(projectId, invoice.id);
      const url = response.initPoint || response.sandboxInitPoint;
      if (!url) throw new Error("Mercado Pago no devolvió un enlace de pago");
      window.location.assign(url);
    } catch (err) {
      setError(err.message || "No se pudo iniciar el pago");
      setSaving(false);
    }
  };

  const subscription = data?.subscription;
  const account = data?.account;
  const project = data?.project;
  const invoices = data?.invoices || [];
  const services = (subscription?.services || []).filter(
    (service) => service.active !== false,
  );
  const openInvoices = invoices.filter((invoice) =>
    ["open", "overdue"].includes(invoice.status),
  );
  const outstanding = openInvoices.reduce(
    (sum, invoice) => sum + Number(invoice.total || 0),
    0,
  );
  const recurringTotal = services
    .filter((service) => service.billingType === "recurring")
    .reduce(
      (sum, service) =>
        sum + Number(service.quantity || 0) * Number(service.unitAmount || 0),
      0,
    );
  const subscriptionStatus = statusMeta(subscription?.status);
  const projectStatus = statusMeta(project?.status);

  if (loading && !data) {
    return (
      <Box
        sx={{
          minHeight: 420,
          display: "grid",
          placeItems: "center",
          borderRadius: 4,
          bgcolor: BRAND.page,
        }}
      >
        <Stack alignItems="center" spacing={1.5}>
          <CircularProgress size={34} sx={{ color: BRAND.orange }} />
          <Typography sx={{ color: BRAND.muted, fontWeight: 750 }}>
            Cargando cuenta y facturación…
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 5,
            p: { xs: 2.5, md: 4 },
            color: "#FFFFFF",
            background:
              "linear-gradient(135deg, #19242F 0%, #253746 58%, #334B5D 100%)",
            boxShadow: "0 22px 55px rgba(25,36,47,0.20)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 260,
              height: 260,
              borderRadius: "50%",
              right: -90,
              top: -120,
              bgcolor: "rgba(243,90,58,0.24)",
              filter: "blur(2px)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 180,
              height: 180,
              borderRadius: "50%",
              right: 110,
              bottom: -130,
              bgcolor: "rgba(255,255,255,0.08)",
            }}
          />

          <Stack
            position="relative"
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ md: "center" }}
            spacing={3}
          >
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1.2 }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    fontWeight: 900,
                    letterSpacing: ".14em",
                    color: "rgba(255,255,255,.68)",
                  }}
                >
                  WEBPROFIT BILLING
                </Typography>
                <Chip
                  size="small"
                  label={subscriptionStatus.label}
                  color={subscriptionStatus.color}
                  sx={{ fontWeight: 900 }}
                />
              </Stack>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 950,
                  fontSize: { xs: "2rem", md: "2.65rem" },
                  letterSpacing: "-.04em",
                }}
              >
                Plan y facturación
              </Typography>
              <Typography
                sx={{ mt: 1, color: "rgba(255,255,255,.72)", maxWidth: 700 }}
              >
                Administrá el abono, los datos de facturación, los servicios
                contratados y el historial de cobros de{" "}
                {project?.name || "este proyecto"}.
              </Typography>
              <Stack
                direction="row"
                spacing={1.2}
                flexWrap="wrap"
                sx={{ mt: 2.2 }}
              >
                <Chip
                  icon={<StorefrontRoundedIcon />}
                  label={project?.name || "Proyecto"}
                  sx={{
                    bgcolor: "rgba(255,255,255,.12)",
                    color: "#FFFFFF",
                    fontWeight: 800,
                  }}
                />
                <Chip
                  icon={<BusinessRoundedIcon />}
                  label={`Acceso: ${data?.memberRole || "—"}`}
                  sx={{
                    bgcolor: "rgba(255,255,255,.12)",
                    color: "#FFFFFF",
                    fontWeight: 800,
                  }}
                />
              </Stack>
            </Box>

            <Stack alignItems={{ xs: "stretch", md: "flex-end" }} spacing={1.2}>
              <Button
                onClick={load}
                disabled={loading || saving}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <RefreshRoundedIcon />
                  )
                }
                variant="outlined"
                sx={{
                  color: "#FFFFFF",
                  borderColor: "rgba(255,255,255,.38)",
                  borderRadius: 999,
                  px: 2.2,
                  fontWeight: 900,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#FFFFFF",
                    bgcolor: "rgba(255,255,255,.08)",
                  },
                }}
              >
                Actualizar
              </Button>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,.58)" }}
              >
                Estado de la página: {projectStatus.label}
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        {error ? (
          <Alert
            severity="error"
            onClose={() => setError("")}
            sx={{ borderRadius: 3, border: "1px solid rgba(211,67,67,.16)" }}
          >
            {error}
          </Alert>
        ) : null}

        {message ? (
          <Alert
            severity="success"
            onClose={() => setMessage("")}
            sx={{ borderRadius: 3, border: "1px solid rgba(46,125,50,.16)" }}
          >
            {message}
          </Alert>
        ) : null}

        <Grid container spacing={2.25}>
          <Grid item xs={12} sm={6} xl={3}>
            <MetricCard
              icon={<PaymentsRoundedIcon />}
              label="ABONO RECURRENTE"
              value={money(recurringTotal, subscription?.currency)}
              helper="Importe base de los servicios recurrentes activos."
              tone="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} xl={3}>
            <MetricCard
              icon={<CreditCardRoundedIcon />}
              label="SALDO PENDIENTE"
              value={money(outstanding, subscription?.currency)}
              helper={`${openInvoices.length} factura${openInvoices.length === 1 ? "" : "s"} por conciliar.`}
              tone={outstanding > 0 ? "danger" : "default"}
            />
          </Grid>
          <Grid item xs={12} sm={6} xl={3}>
            <MetricCard
              icon={<ReceiptLongRoundedIcon />}
              label="SERVICIOS ACTIVOS"
              value={services.length}
              helper="Servicios incluidos actualmente en el abono."
            />
          </Grid>
          <Grid item xs={12} sm={6} xl={3}>
            <MetricCard
              icon={<CalendarMonthRoundedIcon />}
              label="PRÓXIMA RENOVACIÓN"
              value={date(
                subscription?.currentPeriodEnd || subscription?.nextBillingDate,
              )}
              helper={`Día de renovación configurado: ${subscription?.renewalDay || subscriptionForm.renewalDay || 1}.`}
            />
          </Grid>
        </Grid>

        {superAdmin ? (
          <Paper
            component="form"
            onSubmit={saveBilling}
            elevation={0}
            sx={{ ...PANEL_SX, p: { xs: 2.5, md: 3.25 } }}
          >
            <SectionHeading
              eyebrow="ADMINISTRACIÓN"
              title="Cuenta y reglas del abono"
              description="Datos fiscales, contacto de cobro y comportamiento de activación del proyecto."
              action={
                <Button
                  type="submit"
                  disabled={saving}
                  startIcon={
                    saving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveRoundedIcon />
                    )
                  }
                  variant="contained"
                  sx={{
                    bgcolor: BRAND.orange,
                    borderRadius: 999,
                    px: 2.6,
                    textTransform: "none",
                    fontWeight: 900,
                    boxShadow: "0 10px 24px rgba(243,90,58,.24)",
                    "&:hover": { bgcolor: "#DE4D30" },
                  }}
                >
                  Guardar cambios
                </Button>
              }
            />

            <Divider sx={{ my: 2.5 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Nombre comercial"
                  value={accountForm.businessName || ""}
                  onChange={(event) =>
                    setAccountForm((current) => ({
                      ...current,
                      businessName: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Razón social"
                  value={accountForm.legalName || ""}
                  onChange={(event) =>
                    setAccountForm((current) => ({
                      ...current,
                      legalName: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="CUIT / identificación fiscal"
                  value={accountForm.taxId || ""}
                  onChange={(event) =>
                    setAccountForm((current) => ({
                      ...current,
                      taxId: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Email de facturación"
                  type="email"
                  value={accountForm.billingEmail || ""}
                  onChange={(event) =>
                    setAccountForm((current) => ({
                      ...current,
                      billingEmail: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  value={accountForm.phone || ""}
                  onChange={(event) =>
                    setAccountForm((current) => ({
                      ...current,
                      phone: event.target.value,
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Estado del abono"
                  value={subscriptionForm.status || "pending_activation"}
                  onChange={(event) =>
                    setSubscriptionForm((current) => ({
                      ...current,
                      status: event.target.value,
                    }))
                  }
                >
                  <MenuItem value="pending_activation">
                    Pendiente de activación
                  </MenuItem>
                  <MenuItem value="active">Activo</MenuItem>
                  <MenuItem value="past_due">Con deuda</MenuItem>
                  <MenuItem value="suspended">Suspendido</MenuItem>
                  <MenuItem value="cancelled">Cancelado</MenuItem>
                  <MenuItem value="exempt">Bonificado</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Modo de activación"
                  value={subscriptionForm.activationMode || "after_payment"}
                  onChange={(event) =>
                    setSubscriptionForm((current) => ({
                      ...current,
                      activationMode: event.target.value,
                    }))
                  }
                >
                  <MenuItem value="after_payment">Automática al pagar</MenuItem>
                  <MenuItem value="manual">Manual</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  label="Gracia (días)"
                  type="number"
                  value={subscriptionForm.graceDays ?? 5}
                  onChange={(event) =>
                    setSubscriptionForm((current) => ({
                      ...current,
                      graceDays: event.target.value,
                    }))
                  }
                  inputProps={{ min: 0, max: 60 }}
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  label="Día de renovación"
                  type="number"
                  value={subscriptionForm.renewalDay ?? 1}
                  onChange={(event) =>
                    setSubscriptionForm((current) => ({
                      ...current,
                      renewalDay: event.target.value,
                    }))
                  }
                  inputProps={{ min: 1, max: 28 }}
                />
              </Grid>
            </Grid>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.2}
              sx={{ mt: 2.5 }}
            >
              <Button
                disabled={saving}
                startIcon={<CheckCircleRoundedIcon />}
                onClick={() =>
                  run(
                    () =>
                      billingApi.updateProject(projectId, {
                        subscription: { status: "active" },
                      }),
                    "Página activada.",
                  )
                }
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  color: "success.main",
                  bgcolor: "rgba(46,125,50,.08)",
                  px: 2,
                }}
              >
                Activar página
              </Button>
              <Button
                disabled={saving}
                color="warning"
                startIcon={<PauseCircleRoundedIcon />}
                onClick={() =>
                  run(
                    () =>
                      billingApi.updateProject(projectId, {
                        subscription: { status: "suspended" },
                      }),
                    "Página suspendida.",
                  )
                }
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 900,
                  bgcolor: "rgba(237,108,2,.08)",
                  px: 2,
                }}
              >
                Suspender página
              </Button>
            </Stack>
          </Paper>
        ) : null}

        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} lg={5}>
            <Paper
              elevation={0}
              sx={{ ...PANEL_SX, p: { xs: 2.5, md: 3.25 }, height: "100%" }}
            >
              <SectionHeading
                eyebrow="PLAN"
                title="Servicios contratados"
                description="Detalle de conceptos incluidos dentro del abono del proyecto."
              />

              {superAdmin ? (
                <Box
                  component="form"
                  onSubmit={assignService}
                  sx={{
                    mt: 2.5,
                    p: 2,
                    borderRadius: 3,
                    bgcolor: BRAND.page,
                    border: `1px solid ${BRAND.border}`,
                  }}
                >
                  <Stack spacing={1.5}>
                    <TextField
                      select
                      label="Seleccionar servicio"
                      value={serviceForm.serviceId}
                      onChange={(event) => {
                        const serviceId = event.target.value;
                        const service = (data?.availableServices || []).find(
                          (item) => item.id === serviceId,
                        );
                        setServiceForm((current) => ({
                          ...current,
                          serviceId,
                          unitAmount: service?.defaultAmount ?? "",
                        }));
                      }}
                      required
                      fullWidth
                    >
                      {(data?.availableServices || []).map((service) => (
                        <MenuItem key={service.id} value={service.id}>
                          {service.name} ·{" "}
                          {money(service.defaultAmount, service.currency)}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                    >
                      <TextField
                        label="Cantidad"
                        type="number"
                        value={serviceForm.quantity}
                        onChange={(event) =>
                          setServiceForm((current) => ({
                            ...current,
                            quantity: event.target.value,
                          }))
                        }
                        inputProps={{ min: 0.01, step: 0.01 }}
                        fullWidth
                      />
                      <TextField
                        label="Importe unitario"
                        type="number"
                        value={serviceForm.unitAmount}
                        onChange={(event) =>
                          setServiceForm((current) => ({
                            ...current,
                            unitAmount: event.target.value,
                          }))
                        }
                        inputProps={{ min: 0, step: 0.01 }}
                        fullWidth
                      />
                    </Stack>
                    <Button
                      type="submit"
                      disabled={saving || !serviceForm.serviceId}
                      startIcon={<AddRoundedIcon />}
                      variant="outlined"
                      sx={{
                        alignSelf: "flex-start",
                        borderRadius: 999,
                        textTransform: "none",
                        fontWeight: 900,
                        borderColor: BRAND.dark,
                        color: BRAND.dark,
                      }}
                    >
                      Agregar al abono
                    </Button>
                  </Stack>
                </Box>
              ) : null}

              <Divider sx={{ my: 2.5 }} />

              <Stack spacing={1.25}>
                {services.map((service) => {
                  const total =
                    Number(service.quantity || 0) *
                    Number(service.unitAmount || 0);
                  return (
                    <Box
                      key={service.id}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: `1px solid ${BRAND.border}`,
                        transition: "transform .18s ease, box-shadow .18s ease",
                        "&:hover": {
                          transform: "translateY(-1px)",
                          boxShadow: "0 10px 24px rgba(25,36,47,.07)",
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={1.5}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Stack
                            direction="row"
                            spacing={0.8}
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            <Typography
                              sx={{ fontWeight: 950, color: BRAND.dark }}
                            >
                              {service.name}
                            </Typography>
                            <Chip
                              size="small"
                              label={
                                service.billingType === "recurring"
                                  ? billingCycleLabel(service.billingCycle)
                                  : "Pago único"
                              }
                              sx={{ fontWeight: 800, height: 23 }}
                            />
                          </Stack>
                          <Typography
                            variant="body2"
                            sx={{ mt: 0.6, color: BRAND.muted }}
                          >
                            {service.quantity} ×{" "}
                            {money(service.unitAmount, service.currency)}
                          </Typography>
                          <Typography
                            sx={{ mt: 0.8, fontWeight: 950, color: BRAND.dark }}
                          >
                            {money(total, service.currency)}
                          </Typography>
                        </Box>
                        {superAdmin ? (
                          <Tooltip title="Quitar servicio del abono">
                            <IconButton
                              color="error"
                              disabled={saving}
                              onClick={() =>
                                run(
                                  () =>
                                    billingApi.removeService(
                                      projectId,
                                      service.id,
                                    ),
                                  "Servicio dado de baja.",
                                )
                              }
                              aria-label={`Quitar ${service.name}`}
                            >
                              <DeleteOutlineRoundedIcon />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </Box>
                  );
                })}

                {!services.length ? (
                  <Box
                    sx={{
                      py: 4,
                      px: 2,
                      textAlign: "center",
                      borderRadius: 3,
                      border: `1px dashed ${BRAND.border}`,
                      bgcolor: BRAND.page,
                    }}
                  >
                    <ReceiptLongRoundedIcon
                      sx={{ fontSize: 42, color: "rgba(25,36,47,.26)" }}
                    />
                    <Typography
                      sx={{ mt: 1, fontWeight: 900, color: BRAND.dark }}
                    >
                      Sin servicios asignados
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, color: BRAND.muted }}
                    >
                      Los conceptos del plan aparecerán en esta sección.
                    </Typography>
                  </Box>
                ) : null}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={7}>
            <Paper
              elevation={0}
              sx={{ ...PANEL_SX, p: { xs: 2.5, md: 3.25 }, height: "100%" }}
            >
              <SectionHeading
                eyebrow="COBROS"
                title="Historial de facturas"
                description="Documentos internos, vencimientos y conciliación de pagos."
                action={
                  superAdmin ? (
                    <Box component="form" onSubmit={createInvoice}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                      >
                        <TextField
                          size="small"
                          label="Vence en días"
                          type="number"
                          value={invoiceForm.dueDays}
                          onChange={(event) =>
                            setInvoiceForm((current) => ({
                              ...current,
                              dueDays: event.target.value,
                            }))
                          }
                          inputProps={{ min: 0, max: 365 }}
                          sx={{ width: { xs: "100%", sm: 145 } }}
                        />
                        <Button
                          type="submit"
                          disabled={saving || !services.length}
                          startIcon={<ReceiptLongRoundedIcon />}
                          variant="contained"
                          sx={{
                            bgcolor: BRAND.orange,
                            borderRadius: 999,
                            px: 2.2,
                            textTransform: "none",
                            fontWeight: 900,
                            "&:hover": { bgcolor: "#DE4D30" },
                          }}
                        >
                          Emitir factura
                        </Button>
                      </Stack>
                    </Box>
                  ) : null
                }
              />

              <TableContainer
                sx={{
                  mt: 2.5,
                  borderRadius: 3,
                  border: `1px solid ${BRAND.border}`,
                  overflowX: "auto",
                }}
              >
                <Table sx={{ minWidth: 760 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#F7F8FA" }}>
                      {[
                        "Factura",
                        "Emisión",
                        "Vencimiento",
                        "Estado",
                        "Total",
                        "Acciones",
                      ].map((label, index) => (
                        <TableCell
                          key={label}
                          align={index >= 4 ? "right" : "left"}
                          sx={{
                            py: 1.5,
                            color: BRAND.muted,
                            fontSize: 12,
                            fontWeight: 900,
                            textTransform: "uppercase",
                            letterSpacing: ".07em",
                            borderBottom: `1px solid ${BRAND.border}`,
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((invoice) => {
                      const invoiceStatus = statusMeta(invoice.status);
                      const fiscalStatus = statusMeta(
                        invoice.fiscal?.status || "pending",
                      );
                      return (
                        <TableRow
                          key={invoice.id}
                          hover
                          sx={{
                            "&:last-child td": { borderBottom: 0 },
                            "&:hover": { bgcolor: "rgba(25,36,47,.025)" },
                          }}
                        >
                          <TableCell sx={{ py: 1.7 }}>
                            <Typography
                              sx={{ fontWeight: 950, color: BRAND.dark }}
                            >
                              {invoice.invoiceNumber}
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={0.6}
                              alignItems="center"
                              sx={{ mt: 0.45 }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: BRAND.muted }}
                              >
                                Fiscal
                              </Typography>
                              <Chip
                                size="small"
                                label={fiscalStatus.label}
                                color={fiscalStatus.color}
                                variant="outlined"
                                sx={{
                                  height: 21,
                                  fontSize: 11,
                                  fontWeight: 800,
                                }}
                              />
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ color: BRAND.muted }}>
                            {date(invoice.issueDate)}
                          </TableCell>
                          <TableCell sx={{ color: BRAND.muted }}>
                            {date(invoice.dueDate)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={invoiceStatus.label}
                              color={invoiceStatus.color}
                              sx={{ fontWeight: 850 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              sx={{ fontWeight: 950, color: BRAND.dark }}
                            >
                              {money(invoice.total, invoice.currency)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              justifyContent="flex-end"
                              spacing={0.5}
                            >
                              <Tooltip title="Imprimir documento">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    printInvoice(invoice, account, project)
                                  }
                                  aria-label={`Imprimir ${invoice.invoiceNumber}`}
                                >
                                  <PrintRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {["open", "overdue"].includes(invoice.status) ? (
                                <Button
                                  size="small"
                                  disabled={saving}
                                  startIcon={<CreditCardRoundedIcon />}
                                  onClick={() => payInvoice(invoice)}
                                  variant="contained"
                                  sx={{
                                    bgcolor: BRAND.dark,
                                    borderRadius: 999,
                                    textTransform: "none",
                                    fontWeight: 900,
                                    boxShadow: "none",
                                    "&:hover": {
                                      bgcolor: "#0F171E",
                                      boxShadow: "none",
                                    },
                                  }}
                                >
                                  Pagar
                                </Button>
                              ) : null}
                              {superAdmin &&
                              ["open", "overdue"].includes(invoice.status) ? (
                                <Button
                                  size="small"
                                  color="success"
                                  disabled={saving}
                                  onClick={() =>
                                    run(
                                      () =>
                                        billingApi.registerManualPayment(
                                          projectId,
                                          invoice.id,
                                        ),
                                      "Pago registrado y página conciliada.",
                                    )
                                  }
                                  sx={{
                                    borderRadius: 999,
                                    textTransform: "none",
                                    fontWeight: 900,
                                  }}
                                >
                                  Marcar cobrada
                                </Button>
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {!invoices.length ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          sx={{ py: 7, textAlign: "center" }}
                        >
                          <ReceiptLongRoundedIcon
                            sx={{ fontSize: 44, color: "rgba(25,36,47,.24)" }}
                          />
                          <Typography
                            sx={{ mt: 1, fontWeight: 900, color: BRAND.dark }}
                          >
                            Todavía no hay facturas
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ mt: 0.5, color: BRAND.muted }}
                          >
                            Cuando se emita el primer documento aparecerá en
                            este historial.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            p: 2.3,
            borderRadius: 3,
            border: "1px solid rgba(2,136,209,.18)",
            bgcolor: "rgba(2,136,209,.045)",
          }}
        >
          <Stack direction="row" spacing={1.4} alignItems="flex-start">
            <InfoOutlinedIcon sx={{ color: "info.main", mt: 0.2 }} />
            <Box>
              <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                Documentación de cobro y emisión fiscal
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.45, color: BRAND.muted }}>
                Estas facturas funcionan como documentos internos de cobro. El
                bloque fiscal queda preparado para almacenar CAE, numeración y
                PDF de un proveedor externo. La emisión fiscal ante ARCA
                requiere integrar credenciales y certificados por separado.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {subscription?.status === "past_due" ||
        subscription?.status === "suspended" ? (
          <Alert
            severity="warning"
            icon={<ErrorOutlineRoundedIcon />}
            sx={{ borderRadius: 3 }}
          >
            El abono presenta una incidencia. Revisá las facturas pendientes y
            el estado de la suscripción antes de reactivar el proyecto.
          </Alert>
        ) : null}
      </Stack>
    </Box>
  );
}